import { Button, Tooltip } from "@mui/material";
import React, { useCallback, useContext, useEffect, useMemo } from "react";

import { UserContext } from "layouts/SlapshotMainLayout";

import styles from "./Guide.module.scss";

type Props = {
  id: string;
  title: string;
  description: string;
  open: boolean;
  children: React.ReactElement<Record<string, unknown>>;
  placement:
    | "bottom-end"
    | "bottom-start"
    | "bottom"
    | "left-end"
    | "left-start"
    | "left"
    | "right-end"
    | "right-start"
    | "right"
    | "top-end"
    | "top-start"
    | "top";
  enableHoverListener?: boolean;
};

const Guide = ({
  id,
  title,
  description,
  open,
  children,
  placement,
  enableHoverListener,
}: Props) => {
  const user = useContext(UserContext);
  const tips = useMemo(
    () => user?.preferences?.tips || [],
    [user?.preferences?.tips],
  );
  const showTip =
    open &&
    user?.preferences !== undefined &&
    !tips.includes(id) &&
    !user?.preferences?.hideTips;

  const listener = useCallback(() => {
    user?.setPreferences("tips", id, true);
  }, [id, user]);

  // Show only one tip at a time
  useEffect(() => {
    if (!showTip) return () => {};

    const interval = setInterval(() => {
      const renderedGuides = document.querySelectorAll('[role="tooltip"]');
      if (
        renderedGuides.length &&
        renderedGuides[0].getAttribute("id") === id
      ) {
        const child = renderedGuides[0].firstElementChild as HTMLElement | null;
        if (child) {
          child.style.visibility = "visible";
          clearInterval(interval);
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [id, showTip]);

  return (
    <Tooltip
      open={showTip}
      id={id}
      arrow
      disableFocusListener
      disableHoverListener
      placement={placement}
      slotProps={{
        tooltip: {
          sx: {
            padding: "0px",
            backgroundColor: "unset",
            fontFamily: "Inter",
            visibility: "hidden",
          },
        },
        arrow: {
          sx: {
            color: "#5555DB",
          },
        },
      }}
      title={
        <div className={styles.container}>
          <div className={styles.title}>{title}</div>
          <div className={styles.description}>
            {description.split("\\n").map((line, index) => (
              <React.Fragment key={index}>
                {line}
                <br />
              </React.Fragment>
            ))}
          </div>
          <div className={styles.btns}>
            <Button
              className={styles.btn}
              onClick={() => {
                user?.setPreferences("tips", id, true);
              }}
            >
              Got it
            </Button>
            <Button
              className={styles.btn}
              onClick={() => {
                user?.setPreferences("hideTips", true, false);
              }}
            >
              Don't show more tips
            </Button>
          </div>
        </div>
      }
    >
      {showTip
        ? React.cloneElement(children, {
            onClick: (e) => {
              if (children.props.onClick) {
                (children.props.onClick as React.MouseEventHandler)(e);
              }
              listener();
            },
            ...(enableHoverListener && {
              onMouseEnter: (e) => {
                if (children.props.onMouseEnter) {
                  (children.props.onMouseEnter as React.MouseEventHandler)(e);
                }
                listener();
              },
            }),
          })
        : children}
    </Tooltip>
  );
};

export default Guide;
